// src/savesClient.js
import { db, auth } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from 'firebase/firestore';
import { hataAlani } from './utils/hataAlani';
import { klipPosterUrl } from "./utils/klipAlanlari";

/** Belirli bir içeriğin kaydedilip kaydedilmediğini getirir. */
export async function isSaved(contentId: string | null | undefined) {
  const user = auth.currentUser;
  if (!user || !contentId) return false;
  const ref = doc(db, `users/${user.uid}/saved/${contentId}`);
  const snap = await getDoc(ref);
  return snap.exists();
}

/** Toggle: kayıtlıysa siler; değilse kaydeder. Basit metadata da bırakıyoruz. */
export async function toggleSave({
  contentId,
  type = 'post',          // 'post' | 'clip' | 'story'
  authorId,
  mediaUrl,
  caption,
  isCarousel = false,     // IG paritesi — grid'de çoklu medya rozeti için
}: {
  contentId?: string | null;
  /** Yorumdaki üçlü, sözleşmenin kendisi hâline getirildi. */
  type?: 'post' | 'clip' | 'story';
  authorId?: string | null;
  mediaUrl?: string | null;
  caption?: string | null;
  isCarousel?: boolean;
}) {
  const user = auth.currentUser;
  if (!user || !contentId) return { saved: false };

  const ref = doc(db, `users/${user.uid}/saved/${contentId}`);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    await deleteDoc(ref);
    return { saved: false };
  }

  await setDoc(ref, {
    contentId,
    type,
    authorId: authorId || null,
    mediaUrl: mediaUrl || null,
    caption: caption || '',
    isCarousel: isCarousel === true,
    createdAt: serverTimestamp(),
  });
  return { saved: true };
}

/**
 * ══════════════════════════════════════════════════════════════════════════
 * HİDRASYON — kaydedilenler DONMUŞ KOPYA değil, CANLI içerik
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ ÖLÇÜLEN KUSUR (2026-08-24): `listSaved` YALNIZ `users/{uid}/saved`
 * belgelerini okuyup `toggleSave` ANINDA dondurulmuş `mediaUrl`/`caption`/
 * `authorId` alanlarını döndürüyordu; kaynak belge (`posts/{id}` ya da
 * `clips/{id}`) HİÇ okunmuyordu. Tıklama yolu (`ProfileMobile.openFromSaved`)
 * bu alanlardan sentetik bir öğe kurup görüntüleyiciyi açıyor — varlık
 * kontrolü YOKTU. Sonuç: SİLİNMİŞ gönderi kapağıyla duruyor ve AÇILIYOR;
 * açıklaması düzenlenmiş gönderi ESKİ metni gösteriyor.
 *
 * Emsal aynı üründe zaten vardı: `services/homeFeedMaterialized.ts`
 * `if (!snap.exists()) continue;`. Aynı ürün, iki farklı kuraldı.
 *
 * ── ÜÇ HÂL AYRI (kanon: TEMİZ · İHLAL · ÖLÇEMEDİM) ───────────────────────
 *   TEMİZ     belge okundu          → alanlar CANLI belgeden tazelenir
 *   İHLAL     yok / izin reddedildi → öğe ELENİR
 *   ÖLÇEMEDİM geçici hata (ağ/kota) → donmuş kopya KORUNUR + gürültülü log
 *
 * Üçüncüsünü ikinciye katmak, tek bir ağ kesintisinde kullanıcının TÜM
 * kaydedilenlerini "silinmiş" sayıp ızgarayı boşaltırdı — var olmayan bir
 * kaybı gerçek gibi göstermek. Tersi de doğru değil: `permission-denied`
 * saldırgan-tetiklenebilir değil ve KAPALI yönün bedeli geri alınabilir
 * (yalnız RENDER elenir; `users/{uid}/saved` belgesi SİLİNMEZ — yazar
 * gizliliğini kaldırınca öğe kendiliğinden geri gelir). Açık yönün bedeli
 * ise silinmiş/erişilemez içeriğin kapağını göstermek.
 *
 * ── MALİYET (sessiz valf YOK) ────────────────────────────────────────────
 * Sayfa başına EK okuma = sayfadaki öğe sayısı (varsayılan `pageSize` 18).
 * KIRPMA YAPILMIYOR — sayfadaki her öğe hidrate edilir, yani "log'a
 * yazılmayan atlanan" diye bir sınıf yok. Elenen her öğe `console.warn` ile
 * sayılır. `nextCursor` HAM `saved` belgelerinden türediği için eleme
 * sayfalamayı kaydırmaz (sayfa küçülür, imleç yerinde kalır).
 *
 * `cursor` bir Firestore belge anlık görüntüsüdür — `nextCursor` olarak
 * DÖNDÜRDÜĞÜMÜZ şeyin ta kendisi (`docs[docs.length - 1]`). Varsayılan `null`
 * olduğu için TS parametreyi `null` çıkarıyordu; çağıran gerçek bir cursor
 * geçince "null bekleniyordu" diye düşüyordu. İmza artık DÖNÜŞLE tutarlı.
 */

/** Kaynak belgenin koleksiyonu — kaydedilen `type` alanından türer. */
function kaynakKoleksiyon(type: string): 'posts' | 'clips' {
  return type === 'clip' ? 'clips' : 'posts';
}

/**
 * Bir okuma hatasının "içerik erişilemez" mi yoksa "ölçemedim" mi olduğunu
 * ayırır. Firestore, var olmayan bir belgede kural `resource.data`ya
 * dokunduğu için `permission-denied` da döndürebilir — bu yüzden iki kod da
 * ERİŞİLEMEZ sayılır.
 */
function erisilemezHata(e: unknown): boolean {
  const kod = hataAlani(e, 'code');
  return kod === 'permission-denied' || kod === 'not-found';
}

/**
 * Boş olmayan metin ya da `null`. Eski eşlemedeki `x.alan || null` ile AYNI
 * davranış; farkı, `unknown` üzerinden okunabilmesi (`any` eklemeden).
 */
function metin(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

/** Canlı belgeden tazelenen alanlar. Tür başına alan adları FARKLI. */
function canliAlanlar(type: string, veri: Record<string, unknown>) {
  if (kaynakKoleksiyon(type) === 'clips') {
    // 🔴 GÖZLE+KODLA ÖLÇÜLDÜ (2026-08-25): burası klip için VİDEO url'i
    // döndürüyordu (`v3_r2Url` = R2/HLS adresi) ve `SavedGrid` onu
    // `<img src={...}>` içine koyuyordu. Yani kaydedilenlerde klip karosu
    // DAİMA boştu (`onError` → düz zemin).
    //
    // Kardeş ızgaraların ikisi de doğru alanı okuyor: `UserPosts.tsx`
    // (`v3_posterUrl`) ve `TaggedGridLite.tsx`. Buradaki yorum
    // *"clipsRepo ile aynı sıra"* diyordu ama `clipsRepo` orada İKİ AYRI
    // zincir tutuyor — video (`src`) ve kapak (`posterUrl`); buraya yalnız
    // video zinciri kopyalanmış, kapak zinciri hiç kopyalanmamıştı.
    //
    // ⚠️ Ve bu kusuru bu deponun KENDİ testi doğru diye kilitliyordu
    // (`kaydedilenHidrasyon.test.ts` → `expect(mediaUrl).toBe("….m3u8")`).
    // Kapı, koruduğu şeyin yanlış olduğunu göremiyordu; test de düzeltildi.
    // 🧭 Bir kapının yeşil olması, ölçtüğü şeyin doğru olduğunu göstermez.
    return {
      mediaUrl: klipPosterUrl(veri) || metin(veri.v3_r2Url) || metin(veri.mediaUrl) || metin(veri.videoUrl),
      caption: typeof veri.aciklama === 'string' ? veri.aciklama : '',
      authorId: metin(veri.authorId),
      isCarousel: false, // klipte karusel yok
    };
  }
  return {
    mediaUrl: metin(veri.mediaUrl),
    caption: typeof veri.mesaj === 'string' ? veri.mesaj : '',
    authorId: metin(veri.authorId),
    isCarousel: Array.isArray(veri.mediaItems) && veri.mediaItems.length > 1,
  };
}

/** Kaydedilenleri getirir (sayfalı). Sadece kendi hesabın için çalışır. */
export async function listSaved({
  pageSize = 18,
  cursor = null,
}: {
  pageSize?: number;
  cursor?: import("firebase/firestore").QueryDocumentSnapshot | null;
} = {}) {
  const user = auth.currentUser;
  if (!user) return { items: [], nextCursor: null };

  const col = collection(db, 'users', user.uid, 'saved');
  const parts: import('firebase/firestore').QueryConstraint[] = [orderBy('createdAt', 'desc'), limit(pageSize)];
  if (cursor) parts.push(startAfter(cursor));
  const q = query(col, ...parts);

  const snap = await getDocs(q);
  const docs = snap.docs;

  const donmus = docs.map((d) => {
    const x = d.data() || {};
    // contentId = doc id
    return {
      contentId: d.id,
      type: (x.type || 'post') as string,
      mediaUrl: (x.mediaUrl || null) as string | null,
      caption: (x.caption || '') as string,
      authorId: (x.authorId || null) as string | null,
      createdAt: x.createdAt || null,
      // ⚠️ EKSİK OKUMA (2026-08-05) — `saveContent` bunu satır 52'de YAZIYOR
      // (`usePostLogic:154` → mediaItems.length > 1), ama bu eşleme onu
      // döndürmüyordu. Sonuç: `SavedGrid`in karusel rozeti hiç TRUE olamıyordu.
      // Tasarım tercihi değil, unutulmuş alan — yazan vardı, okuyan yoktu.
      isCarousel: x.isCarousel === true,
    };
  });

  // HİDRASYON — kaynak belgeyi oku (bkz. üstteki blok). Sayfa başına
  // `donmus.length` ek okuma; kırpma YOK.
  const kaynaklar = await Promise.all(
    donmus.map((it) =>
      getDoc(doc(db, kaynakKoleksiyon(it.type), it.contentId))
        .then((belge) => ({ belge, hata: null as unknown }))
        .catch((hata: unknown) => ({ belge: null, hata })),
    ),
  );

  const items: typeof donmus = [];
  let elenen = 0;
  let olcemedim = 0;
  for (let i = 0; i < donmus.length; i++) {
    const it = donmus[i];
    // `belge` adı BİLEREK: dıştaki `snap` (sayfa sorgusunun sonucu) gölgelenmesin.
    const { belge, hata } = kaynaklar[i];

    if (hata) {
      if (erisilemezHata(hata)) { elenen++; continue; }          // İHLAL
      olcemedim++;                                                // ÖLÇEMEDİM
      items.push(it);                                             // donmuş kopya kalır
      continue;
    }
    if (!belge || !belge.exists()) { elenen++; continue; }        // İHLAL: silinmiş
    const veri = (belge.data() || {}) as Record<string, unknown>;
    if (veri.visible === false) { elenen++; continue; }           // moderasyon gizlemesi
    items.push({ ...it, ...canliAlanlar(it.type, veri) });        // TEMİZ
  }

  if (elenen > 0 || olcemedim > 0) {
    // SESSİZ VALF YOK: ne elendiğini ve ne ÖLÇÜLEMEDİĞİNİ ayrı ayrı söyle.
    // eslint-disable-next-line no-console
    console.warn(
      `[listSaved] hidrasyon: ${donmus.length} kayıttan ${elenen} elendi ` +
        `(silinmiş/erişilemez), ${olcemedim} ölçülemedi (donmuş kopya korundu).`,
    );
  }

  // İmleç HAM `saved` belgelerinden türer — eleme sayfalamayı KAYDIRMAZ.
  const nextCursor = docs.length === pageSize ? docs[docs.length - 1] : null;
  return { items, nextCursor };
}
